import React, { Children, createContext, useContext, useMemo } from 'react';

interface CardContextType {
  hasHeader: boolean;
  spacing: 'none' | 'compact' | 'default' | 'spacious';
}

const CardContext = createContext<CardContextType | null>(null);

export const useCardContext = () => {
  const context = useContext(CardContext);
  return context;
};

const hasCardHeaderRecursive = (children: React.ReactNode): boolean => {
  let found = false;
  Children.forEach(children, (child) => {
    if (found) return;
    if (React.isValidElement(child)) {
      const childType = child.type as React.ComponentType & { displayName?: string; name?: string };
      if (childType?.displayName === 'CardHeader') {
        found = true;
        return;
      }
      if (childType?.name === 'CardHeader') {
        found = true;
        return;
      }
      if (child.props?.children) {
        found = hasCardHeaderRecursive(child.props.children);
      }
    }
  });
  return found;
};

export const CardContextProvider: React.FC<{
  children: React.ReactNode;
  spacing: 'none' | 'compact' | 'default' | 'spacious';
}> = ({ children, spacing }) => {
  const hasHeader = useMemo(() => {
    return hasCardHeaderRecursive(children);
  }, [children]);

  const contextValue = useMemo(() => ({ hasHeader, spacing }), [hasHeader, spacing]);

  return <CardContext.Provider value={contextValue}>{children}</CardContext.Provider>;
};
